import { Controller } from '@hotwired/stimulus';

/**
 * ChatController - Stimulus controller for BaZi chat interactions
 *
 * This controller handles:
 * - Mode selection (personalized/general)
 * - Birth data form collection
 * - Creating chat sessions
 * - Sending messages
 * - Receiving streaming responses
 * - Managing chat UI state
 * - localStorage persistence
 *
 * Usage:
 * <div data-controller="chat"
 *      data-chat-session-url-value="/api/chat/sessions"
 *      data-chat-message-url-value="/api/chat/messages"
 *      data-chat-stream-url-value="/api/chat/messages/stream"
 *      data-chat-person-id-value="123"
 *      data-chat-initial-view-value="mode_selection">
 *   ...
 * </div>
 */
export default class extends Controller {
  static targets = [
    'messages',
    'input',
    'form',
    'sendButton',
    'typingIndicator',
    'modeSelection',
    'birthForm',
    'chatInterface',
    'birthDataForm',
    'birthNameInput',
    'birthSurnameInput',
    'birthDateInput',
    'birthTimeInput',
    'birthPlaceInput',
    'birthGenderInput',
    'birthFormSubmit',
    'birthFormError',
    'changeModeButton',
    'chatTitle',
    'personSelector',
    'personSelect',
    'sessionSidebar',
    'sessionList',
    'sessionEmpty',
  ];

  static values = {
    sessionUrl: String,
    sessionsUrl: String,
    showSessionList: { type: Boolean, default: false },
    listPersonId: { type: Number, default: 0 },
    sessionLabels: { type: Object, default: {} },
    messageUrl: String,
    streamUrl: String,
    personId: Number,
    sessionId: String,
    sessionToken: String,
    streaming: { type: Boolean, default: true },
    initialView: { type: String, default: 'mode_selection' },
    mode: { type: String, default: '' },
    allowModeChange: { type: Boolean, default: true },
    showPersonSelector: { type: Boolean, default: false },
    availablePeople: { type: Array, default: [] },
    birthName: String,
    birthSurname: String,
    birthDate: String,
    birthTime: { type: String, default: '12:00' },
    birthPlace: String,
    birthGender: String,
    debugEnabled: { type: Boolean, default: false },
    // I18n values (passed from server via data attributes)
    titleGeneral: { type: String, default: 'BaZi Chat' },
    titlePersonalized: { type: String, default: 'Chat with {name}' },
    defaultProfile: { type: String, default: 'Profile' },
    errorNoSession: { type: String, default: 'No active chat session.' },
    errorStartFailed: { type: String, default: 'Chat could not be started.' },
    errorConnectionStart: {
      type: String,
      default: 'Connection error while starting the chat.',
    },
    errorSendFailed: { type: String, default: 'Message could not be sent.' },
    errorConnectionSend: {
      type: String,
      default: 'Connection error while sending the message.',
    },
    errorReceiveFailed: {
      type: String,
      default: 'Error receiving the response.',
    },
    errorGeneric: { type: String, default: 'An error has occurred.' },
    debugLabels: { type: Object, default: {} },
    feedbackUrl: { type: String, default: '/api/feedback' },
    feedbackLabels: { type: Object, default: {} },
  };

  // localStorage keys
  static STORAGE_KEY_MODE = 'chat.mode';
  static STORAGE_KEY_BIRTH_DATA = 'chat.birthData';
  static STORAGE_KEY_PERSON_ID = 'chat.personId';

  connect() {
    this.isLoading = false;
    this.currentStreamContent = '';
    this.currentBirthData = null;
    this.userHasScrolledUp = false;
    this.feedbackSubmitted = false;
    this.sessionExpired = false;
    // Remember the default input placeholder so we can restore it after viewing
    // an expired (read-only) session.
    this.defaultPlaceholder = this.hasInputTarget ? this.inputTarget.placeholder : '';

    // Typewriter state
    this.typewriterBuffer = '';
    this.typewriterRendered = 0;
    this.typewriterStreamDone = false;
    this.typewriterAnimationId = null;
    this.typewriterContainer = null;
    this.typewriterLastTimestamp = 0;

    // Set up scroll listener for user-scroll awareness
    this.boundHandleScroll = this.handleUserScroll.bind(this);
    if (this.hasMessagesTarget) {
      this.messagesTarget.addEventListener('scroll', this.boundHandleScroll);
    }

    // Check for external config (window.BaZiChatConfig)
    this.applyExternalConfig();

    // Restore stored state from localStorage
    this.restoreStoredState();

    // With a session list, resume the most recent conversation instead of
    // always creating a fresh session on load.
    if (this.showSessionListValue) {
      this.initializeWithSessionList();
    } else {
      // Show initial view based on configuration
      this.showInitialView();
    }
  }

  disconnect() {
    // Close any active EventSource
    if (this.eventSource) {
      this.eventSource.close();
    }

    // Abort any in-flight streaming request
    if (this.streamAbortController) {
      this.streamAbortController.abort();
      this.streamAbortController = null;
    }

    // Cancel typewriter animation
    if (this.typewriterAnimationId) {
      cancelAnimationFrame(this.typewriterAnimationId);
      this.typewriterAnimationId = null;
    }

    // Remove scroll listener
    if (this.hasMessagesTarget && this.boundHandleScroll) {
      this.messagesTarget.removeEventListener('scroll', this.boundHandleScroll);
    }
  }

  // ============================================
  // External Config Support
  // ============================================

  applyExternalConfig() {
    if (typeof window !== 'undefined' && window.BaZiChatConfig) {
      const config = window.BaZiChatConfig;

      if (config.mode) {
        this.modeValue = config.mode;
      }

      if (config.allowModeChange !== undefined) {
        this.allowModeChangeValue = config.allowModeChange;
      }

      if (config.birthData) {
        const bd = config.birthData;
        if (bd.name) this.birthNameValue = bd.name;
        if (bd.surname) this.birthSurnameValue = bd.surname;
        if (bd.date_of_birth) this.birthDateValue = bd.date_of_birth;
        if (bd.time_of_birth) this.birthTimeValue = bd.time_of_birth;
        if (bd.place_of_birth) this.birthPlaceValue = bd.place_of_birth;
        if (bd.gender) this.birthGenderValue = bd.gender;
      }
    }
  }

  // ============================================
  // localStorage Management
  // ============================================

  restoreStoredState() {
    // Don't restore if we have a person_id (existing person flow)
    if (this.hasPersonIdValue && this.personIdValue) {
      return;
    }

    // Don't restore if mode is already set via props
    if (this.modeValue) {
      return;
    }

    try {
      const storedMode = localStorage.getItem(
        this.constructor.STORAGE_KEY_MODE,
      );
      const storedBirthData = localStorage.getItem(
        this.constructor.STORAGE_KEY_BIRTH_DATA,
      );

      if (storedMode) {
        this.modeValue = storedMode;

        // Update initial view based on stored mode
        if (storedMode === 'general') {
          this.initialViewValue = 'chat';
        } else if (storedMode === 'personalized' && storedBirthData) {
          this.currentBirthData = JSON.parse(storedBirthData);
          this.initialViewValue = 'chat';
        } else if (storedMode === 'personalized') {
          this.initialViewValue = 'birth_form';
        }
      }
    } catch (e) {
      console.warn('Failed to restore chat state from localStorage:', e);
    }
  }

  saveMode(mode) {
    try {
      localStorage.setItem(this.constructor.STORAGE_KEY_MODE, mode);
    } catch (e) {
      console.warn('Failed to save chat mode to localStorage:', e);
    }
  }

  saveBirthData(birthData) {
    try {
      localStorage.setItem(
        this.constructor.STORAGE_KEY_BIRTH_DATA,
        JSON.stringify(birthData),
      );
    } catch (e) {
      console.warn('Failed to save birth data to localStorage:', e);
    }
  }

  clearStoredState() {
    try {
      localStorage.removeItem(this.constructor.STORAGE_KEY_MODE);
      localStorage.removeItem(this.constructor.STORAGE_KEY_BIRTH_DATA);
      localStorage.removeItem(this.constructor.STORAGE_KEY_PERSON_ID);
    } catch (e) {
      console.warn('Failed to clear chat state from localStorage:', e);
    }
  }

  // ============================================
  // View Management
  // ============================================

  showInitialView() {
    // Hide all views first
    this.hideAllViews();

    // If we have a person_id, go directly to chat
    if (this.hasPersonIdValue && this.personIdValue) {
      this.showChatInterface();
      this.initializeSession();
      return;
    }

    // Show based on initial view value
    switch (this.initialViewValue) {
      case 'chat':
        this.showChatInterface();
        // Initialize session based on mode
        if (this.modeValue === 'general') {
          this.initializeGeneralSession();
        } else if (this.modeValue === 'personalized' && this.currentBirthData) {
          this.initializePersonalizedSession(this.currentBirthData);
        }
        break;
      case 'birth_form':
        this.showBirthForm();
        this.prepopulateBirthForm();
        break;
      case 'mode_selection':
      default:
        this.showModeSelection();
        break;
    }
  }

  hideAllViews() {
    if (this.hasModeSelectionTarget) {
      this.modeSelectionTarget.classList.add('hidden');
    }
    if (this.hasBirthFormTarget) {
      this.birthFormTarget.classList.add('hidden');
    }
    if (this.hasChatInterfaceTarget) {
      this.chatInterfaceTarget.classList.add('hidden');
    }
  }

  showModeSelection() {
    this.hideAllViews();
    if (this.hasModeSelectionTarget) {
      this.modeSelectionTarget.classList.remove('hidden');
    }
  }

  showBirthForm() {
    this.hideAllViews();
    if (this.hasBirthFormTarget) {
      this.birthFormTarget.classList.remove('hidden');
    }
  }

  showChatInterface() {
    this.hideAllViews();
    if (this.hasChatInterfaceTarget) {
      this.chatInterfaceTarget.classList.remove('hidden');
    }

    // Show change mode button if allowed
    if (this.allowModeChangeValue && this.hasChangeModeButtonTarget) {
      this.changeModeButtonTarget.classList.remove('hidden');
    }
  }

  // ============================================
  // Mode Selection Handlers
  // ============================================

  selectProfileMode() {
    this.modeValue = 'personalized';
    this.saveMode('personalized');
    this.showBirthForm();
    this.prepopulateBirthForm();
  }

  selectGeneralMode() {
    this.modeValue = 'general';
    this.saveMode('general');
    this.showChatInterface();
    this.initializeGeneralSession();
  }

  // Handle person selection from dropdown
  selectPerson(event) {
    const selectedPersonId = event.target.value;
    if (!selectedPersonId) return;

    // Get the person name from the selected option's data attribute
    const selectedOption = event.target.options[event.target.selectedIndex];
    const personName = selectedOption.dataset.name || this.defaultProfileValue;

    this.modeValue = 'personalized';
    this.saveMode('personalized');

    // Store selected person ID
    this.personIdValue = parseInt(selectedPersonId, 10);

    // Opening a different profile must start fresh — drop the previous
    // conversation and session before showing the new one.
    this.resetConversationState();

    // Show chat interface
    this.showChatInterface();

    // Update chat title with person name
    if (this.hasChatTitleTarget) {
      this.chatTitleTarget.textContent = this.titlePersonalizedValue.replace(
        '{name}',
        personName,
      );
    }

    // Initialize session with person_id
    this.initializeSessionWithPerson(selectedPersonId);
  }

  // Initialize session for a selected existing person
  async initializeSessionWithPerson(personId) {
    try {
      const response = await fetch(this.sessionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          session_type: 'personalized',
          context: {
            person_id: parseInt(personId, 10),
          },
          locale: document.documentElement.lang || 'de-DE',
        }),
      });

      const data = await response.json();

      if (data.success) {
        this.sessionIdValue = data.session_id.toString();
        this.sessionTokenValue = data.session_token;

        // Display greeting message
        if (data.greeting) {
          this.appendMessage('assistant', data.greeting);
        }
      } else {
        console.error('Failed to create chat session with person:', data.error);
        this.showError(this.errorStartFailedValue);
      }
    } catch (error) {
      console.error('Error creating chat session with person:', error);
      this.showError(this.errorConnectionStartValue);
    }
  }

  goBackToModeSelection() {
    this.clearStoredState();
    this.modeValue = '';
    this.showModeSelection();
  }

  changeMode() {
    this.clearStoredState();
    this.modeValue = '';
    this.sessionIdValue = '';
    this.sessionTokenValue = '';
    this.currentBirthData = null;
    this.feedbackSubmitted = false;

    // Reset person ID if it was set from selection
    if (!this.hasPersonIdValue || this.personIdValue === 0) {
      this.personIdValue = 0;
    }

    // Clear messages
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = '';
    }

    // Reset person selector dropdown if it exists
    if (this.hasPersonSelectTarget) {
      this.personSelectTarget.selectedIndex = 0;
    }

    this.showModeSelection();
  }

  // Start a brand-new chat in the SAME mode/profile.
  // Creates a fresh session (new token, empty history) so accumulated
  // keyword/continuity context is dropped — the user is no longer "trapped"
  // in the old conversation. The abandoned session auto-expires server-side.
  startNewChat() {
    this.resetConversationState();
    this.reinitializeSession();
  }

  // Clears the on-screen conversation and all transient per-session state.
  // Shared by startNewChat() and selectPerson() (profile switch).
  resetConversationState() {
    // Abort any in-flight stream and stop the typewriter
    if (this.streamAbortController) {
      this.streamAbortController.abort();
      this.streamAbortController = null;
    }
    this.finishTypewriter();

    // Reset session + transient state
    this.sessionIdValue = '';
    this.sessionTokenValue = '';
    this.currentStreamContent = '';
    this.feedbackSubmitted = false;
    this.currentAssistantDiv = null;
    this.setLoading(false);
    this.setSessionExpired(false);

    // Clear the conversation
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = '';
    }
  }

  // Re-initialize a fresh session using the current mode/profile context.
  reinitializeSession() {
    if (this.hasPersonIdValue && this.personIdValue) {
      this.initializeSession();
    } else if (this.modeValue === 'general') {
      this.initializeGeneralSession();
    } else if (this.modeValue === 'personalized' && this.currentBirthData) {
      this.initializePersonalizedSession(this.currentBirthData);
    } else {
      // No usable context to restart from — fall back to mode selection
      this.showModeSelection();
    }
  }

  // ============================================
  // Session List (sidebar)
  // ============================================

  // On load: fetch the session list and resume the most recent conversation.
  // If there is no history, fall back to the default initial view.
  async initializeWithSessionList() {
    const sessions = await this.refreshSessionList();
    // Resume the most recent still-continuable conversation. Expired chats stay
    // browsable in the sidebar but are not auto-opened (they are read-only).
    const resumable = sessions.find((session) => !session.expired);

    if (resumable) {
      this.hideAllViews();
      this.showChatInterface();
      await this.loadSession(resumable);
    } else {
      this.showInitialView();
    }
  }

  // Fetch the user's sessions (optionally scoped to this page's person) and
  // render them into the sidebar. Returns the array of sessions.
  async refreshSessionList() {
    if (!this.showSessionListValue || !this.hasSessionListTarget) return [];

    try {
      let url = this.sessionsUrlValue;
      if (this.listPersonIdValue) {
        url += `?person_id=${this.listPersonIdValue}`;
      }

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return [];

      const data = await response.json();
      const sessions = data.sessions || [];
      this.renderSessionList(sessions);
      return sessions;
    } catch (error) {
      console.error('Error loading chat sessions:', error);
      return [];
    }
  }

  renderSessionList(sessions) {
    if (!this.hasSessionListTarget) return;

    this.sessionListTarget.innerHTML = '';
    if (this.hasSessionEmptyTarget) {
      this.sessionEmptyTarget.classList.toggle('hidden', sessions.length > 0);
    }

    sessions.forEach((session) => {
      this.sessionListTarget.appendChild(this.createSessionItem(session));
    });

    this.markActiveSession();
  }

  createSessionItem(session) {
    const labels = this.sessionLabelsValue;

    // Container holds the (clickable) select area + a delete button. It is a
    // <div> rather than a <button> so the delete <button> can nest validly.
    const item = document.createElement('div');
    item.dataset.sessionId = String(session.id);
    item.className =
      'session-item group flex items-center gap-1 rounded-lg text-sm text-chat-text hover:bg-chat-elevated transition-colors';

    // Main clickable area — selects/loads the session
    const selectBtn = document.createElement('button');
    selectBtn.type = 'button';
    selectBtn.className = 'flex-1 min-w-0 text-left px-3 py-2 cursor-pointer';
    selectBtn.addEventListener('click', () => this.selectSession(session));

    const title = document.createElement('div');
    title.className = 'truncate font-medium';
    title.textContent = session.title || labels.untitled || 'Conversation';
    selectBtn.appendChild(title);

    // Secondary line: person name (when not scoped to one person) or "general"
    const subtitleText =
      !this.listPersonIdValue && session.person_name
        ? session.person_name
        : session.session_type === 'general'
          ? labels.general || ''
          : '';

    if (subtitleText || session.expired) {
      const meta = document.createElement('div');
      meta.className = 'flex items-center gap-2 mt-0.5';

      if (subtitleText) {
        const sub = document.createElement('span');
        sub.className = 'text-xs text-chat-faint truncate';
        sub.textContent = subtitleText;
        meta.appendChild(sub);
      }

      if (session.expired) {
        const badge = document.createElement('span');
        badge.className =
          'text-[10px] uppercase tracking-wide text-chat-faint border border-chat-border rounded px-1 py-0.5 flex-shrink-0';
        badge.textContent = labels.expired || 'Expired';
        meta.appendChild(badge);
      }

      selectBtn.appendChild(meta);
    }

    item.appendChild(selectBtn);

    // Delete (soft-delete) button
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.title = labels.delete || 'Delete';
    deleteBtn.setAttribute('aria-label', labels.delete || 'Delete');
    deleteBtn.className =
      'flex-shrink-0 mr-1 w-7 h-7 flex items-center justify-center rounded text-chat-faint hover:text-red-400 hover:bg-chat-bg transition-colors cursor-pointer opacity-70 focus:opacity-100 md:opacity-0 md:group-hover:opacity-100';
    deleteBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 7.5h12M9.5 7.5V6a1.5 1.5 0 011.5-1.5h2A1.5 1.5 0 0114.5 6v1.5m2 0v11a1.5 1.5 0 01-1.5 1.5h-6a1.5 1.5 0 01-1.5-1.5v-11"/></svg>';
    deleteBtn.addEventListener('click', (event) =>
      this.deleteSession(session, event),
    );
    item.appendChild(deleteBtn);

    return item;
  }

  // Soft-delete a conversation (hidden from the user, retained for admins).
  async deleteSession(session, event) {
    event.stopPropagation();
    const labels = this.sessionLabelsValue;

    if (!window.confirm(labels.delete_confirm || 'Delete this conversation?')) {
      return;
    }

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content');

      const response = await fetch(`${this.sessionsUrlValue}/${session.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const wasActive = String(session.id) === String(this.sessionIdValue);
      const sessions = await this.refreshSessionList();

      if (wasActive) {
        const resumable = sessions.find((s) => !s.expired);
        if (resumable) {
          this.hideAllViews();
          this.showChatInterface();
          await this.loadSession(resumable);
        } else {
          // Nothing left to resume — start a fresh conversation
          this.resetConversationState();
          this.reinitializeSession();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      this.showError(labels.delete_error || this.errorGenericValue);
    }
  }

  // Jump to a session selected from the sidebar.
  async selectSession(session) {
    // Already viewing this one
    if (String(session.id) === String(this.sessionIdValue)) {
      this.closeSessionListOnMobile();
      return;
    }

    this.hideAllViews();
    this.showChatInterface();
    await this.loadSession(session);
    this.closeSessionListOnMobile();
  }

  // Load a session's history into the chat view and make it the active session.
  async loadSession(session) {
    // Drop any in-flight stream / current conversation
    if (this.streamAbortController) {
      this.streamAbortController.abort();
      this.streamAbortController = null;
    }
    this.finishTypewriter();
    this.feedbackSubmitted = false;
    this.currentStreamContent = '';
    this.currentAssistantDiv = null;
    this.setLoading(false);
    if (this.hasMessagesTarget) this.messagesTarget.innerHTML = '';

    // Set active session + context. The session token is not needed for
    // signed-in users — the API scopes by session_id + the Devise session.
    this.sessionIdValue = String(session.id);
    this.sessionTokenValue = '';
    this.modeValue =
      session.session_type === 'personalized' ? 'personalized' : 'general';
    this.personIdValue = session.person_id || 0;
    this.updateChatTitle(session);

    try {
      const response = await fetch(`${this.sessionUrlValue}/${session.id}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();

      if (data.success && data.session) {
        this.renderHistory(data.session.messages || []);
        this.setSessionExpired(Boolean(data.session.expired));
      } else {
        this.showError(
          this.sessionLabelsValue.load_error || this.errorGenericValue,
        );
      }
    } catch (error) {
      console.error('Error loading session:', error);
      this.showError(
        this.sessionLabelsValue.load_error || this.errorGenericValue,
      );
    }

    this.markActiveSession();
  }

  updateChatTitle(session) {
    if (!this.hasChatTitleTarget) return;

    if (session.session_type === 'personalized' && session.person_name) {
      this.chatTitleTarget.textContent = this.titlePersonalizedValue.replace(
        '{name}',
        session.person_name,
      );
    } else {
      this.chatTitleTarget.textContent = this.titleGeneralValue;
    }
  }

  renderHistory(messages) {
    messages.forEach((message) => {
      this.appendMessage(message.role, message.content);
    });
    this.scrollToBottom();
  }

  // Toggle read-only state when viewing an expired session.
  setSessionExpired(expired) {
    this.sessionExpired = expired;

    if (this.hasInputTarget) {
      this.inputTarget.disabled = expired;
      this.inputTarget.placeholder = expired
        ? this.sessionLabelsValue.expired_notice || this.defaultPlaceholder
        : this.defaultPlaceholder;
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = expired;
    }
  }

  markActiveSession() {
    if (!this.hasSessionListTarget) return;

    this.sessionListTarget
      .querySelectorAll('[data-session-id]')
      .forEach((el) => {
        const active = el.dataset.sessionId === String(this.sessionIdValue);
        el.classList.toggle('bg-chat-elevated', active);
        el.classList.toggle('text-chat-text', active);
      });
  }

  // Mobile: slide the off-canvas sidebar in/out.
  toggleSessionList() {
    if (!this.hasSessionSidebarTarget) return;
    const sidebar = this.sessionSidebarTarget;
    const open = sidebar.classList.contains('translate-x-0');
    sidebar.classList.toggle('translate-x-0', !open);
    sidebar.classList.toggle('-translate-x-full', open);
  }

  closeSessionListOnMobile() {
    if (!this.hasSessionSidebarTarget) return;
    this.sessionSidebarTarget.classList.remove('translate-x-0');
    this.sessionSidebarTarget.classList.add('-translate-x-full');
  }

  // ============================================
  // Birth Form Handling
  // ============================================

  prepopulateBirthForm() {
    // Pre-populate from data attributes or localStorage
    if (this.hasBirthNameInputTarget && this.birthNameValue) {
      this.birthNameInputTarget.value = this.birthNameValue;
    }
    if (this.hasBirthSurnameInputTarget && this.birthSurnameValue) {
      this.birthSurnameInputTarget.value = this.birthSurnameValue;
    }
    if (this.hasBirthDateInputTarget && this.birthDateValue) {
      this.birthDateInputTarget.value = this.birthDateValue;
    }
    if (this.hasBirthTimeInputTarget && this.birthTimeValue) {
      this.birthTimeInputTarget.value = this.birthTimeValue;
    }
    if (this.hasBirthPlaceInputTarget && this.birthPlaceValue) {
      this.birthPlaceInputTarget.value = this.birthPlaceValue;
    }
    if (this.birthGenderValue && this.hasBirthGenderInputTarget) {
      const genderInputs = this.birthGenderInputTargets;
      genderInputs.forEach((input) => {
        if (input.value === this.birthGenderValue) {
          input.checked = true;
        }
      });
    }
  }

  submitBirthForm(event) {
    event.preventDefault();

    // Collect form data
    const name = this.hasBirthNameInputTarget
      ? this.birthNameInputTarget.value.trim()
      : '';
    const surname = this.hasBirthSurnameInputTarget
      ? this.birthSurnameInputTarget.value.trim()
      : '';
    const dateOfBirth = this.hasBirthDateInputTarget
      ? this.birthDateInputTarget.value
      : '';
    const timeOfBirth = this.hasBirthTimeInputTarget
      ? this.birthTimeInputTarget.value
      : '12:00';
    const placeOfBirth = this.hasBirthPlaceInputTarget
      ? this.birthPlaceInputTarget.value.trim()
      : '';

    // Get selected gender
    let gender = '';
    if (this.hasBirthGenderInputTarget) {
      const genderInputs = this.birthGenderInputTargets;
      const selectedGender = genderInputs.find((input) => input.checked);
      gender = selectedGender ? selectedGender.value : '';
    }

    // Validate required fields
    if (!name || !dateOfBirth || !gender) {
      this.showBirthFormError();
      return;
    }

    // Hide error if previously shown
    this.hideBirthFormError();

    // Build birth data object
    const birthData = {
      name,
      surname,
      date_of_birth: dateOfBirth,
      time_of_birth: timeOfBirth,
      place_of_birth: placeOfBirth,
      gender,
    };

    // Save to localStorage
    this.saveBirthData(birthData);
    this.currentBirthData = birthData;

    // Show chat and initialize session
    this.showChatInterface();
    this.initializePersonalizedSession(birthData);
  }

  showBirthFormError() {
    if (this.hasBirthFormErrorTarget) {
      this.birthFormErrorTarget.classList.remove('hidden');
    }
  }

  hideBirthFormError() {
    if (this.hasBirthFormErrorTarget) {
      this.birthFormErrorTarget.classList.add('hidden');
    }
  }

  // ============================================
  // Session Initialization
  // ============================================

  async initializeSession() {
    // This is for existing person flow (person_id provided)
    try {
      const response = await fetch(this.sessionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          session_type: 'personalized',
          source: 'internal',
          context: {
            person_id: this.personIdValue,
          },
          locale: document.documentElement.lang || 'de-DE',
        }),
      });

      const data = await response.json();

      if (data.success) {
        this.sessionIdValue = data.session_id.toString();
        this.sessionTokenValue = data.session_token;

        // Display greeting message
        if (data.greeting) {
          this.appendMessage('assistant', data.greeting);
        }
      } else {
        console.error('Failed to create chat session:', data.error);
        this.showError(this.errorStartFailedValue);
      }
    } catch (error) {
      console.error('Error creating chat session:', error);
      this.showError(this.errorConnectionStartValue);
    }
  }

  async initializeGeneralSession() {
    try {
      const response = await fetch(this.sessionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          session_type: 'general',
          source: 'internal',
          locale: document.documentElement.lang || 'de-DE',
        }),
      });

      const data = await response.json();

      if (data.success) {
        this.sessionIdValue = data.session_id.toString();
        this.sessionTokenValue = data.session_token;

        // Update chat title if target exists
        if (this.hasChatTitleTarget) {
          this.chatTitleTarget.textContent = this.titleGeneralValue;
        }

        // Display greeting message
        if (data.greeting) {
          this.appendMessage('assistant', data.greeting);
        }
      } else {
        console.error('Failed to create general chat session:', data.error);
        this.showError(this.errorStartFailedValue);
      }
    } catch (error) {
      console.error('Error creating general chat session:', error);
      this.showError(this.errorConnectionStartValue);
    }
  }

  async initializePersonalizedSession(birthData) {
    try {
      const response = await fetch(this.sessionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          session_type: 'personalized',
          source: 'internal',
          context: {
            birth_data: birthData,
          },
          locale: document.documentElement.lang || 'de-DE',
        }),
      });

      const data = await response.json();

      if (data.success) {
        this.sessionIdValue = data.session_id.toString();
        this.sessionTokenValue = data.session_token;

        // Update chat title with name if target exists
        if (this.hasChatTitleTarget && birthData.name) {
          this.chatTitleTarget.textContent =
            this.titlePersonalizedValue.replace('{name}', birthData.name);
        }

        // Display greeting message
        if (data.greeting) {
          this.appendMessage('assistant', data.greeting);
        }
      } else {
        console.error(
          'Failed to create personalized chat session:',
          data.error,
        );
        this.showError(this.errorStartFailedValue);
      }
    } catch (error) {
      console.error('Error creating personalized chat session:', error);
      this.showError(this.errorConnectionStartValue);
    }
  }

  // ============================================
  // Message Handling
  // ============================================

  async submit(event) {
    event.preventDefault();

    const message = this.inputTarget.value.trim();
    if (!message || this.isLoading) return;

    // Expired sessions are read-only — the input is disabled, but guard anyway
    if (this.sessionExpired) return;

    // Check for session
    if (!this.sessionIdValue) {
      this.showError(this.errorNoSessionValue);
      return;
    }

    // Flush any running typewriter before starting a new message
    if (this.typewriterContainer) {
      this.finishTypewriter();
    }

    // Reset scroll tracking for new message
    this.userHasScrolledUp = false;

    // Clear input and show user message
    this.inputTarget.value = '';
    this.appendMessage('user', message);

    // Disable input while processing
    this.setLoading(true);

    if (this.streamingValue) {
      await this.sendStreamingMessage(message);
    } else {
      await this.sendMessage(message);
    }
  }

  async sendMessage(message) {
    try {
      const response = await fetch(this.messageUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          session_id: this.sessionIdValue,
          session_token: this.sessionTokenValue,
          message: message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        this.appendMessage('assistant', data.message.content, data.debug);
        this.refreshSessionList();
      } else {
        this.showError(data.error || this.errorSendFailedValue);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      this.showError(this.errorConnectionSendValue);
    } finally {
      this.setLoading(false);
    }
  }

  async sendStreamingMessage(message) {
    this.currentStreamContent = '';
    this.userHasScrolledUp = false;

    this.pendingDebugData = null;

    // Create a placeholder for the streaming response
    const assistantDiv = this.createMessageElement('assistant', '');
    this.messagesTarget.appendChild(assistantDiv);
    this.currentAssistantDiv = assistantDiv;
    const contentContainer = assistantDiv.querySelector(
      '[data-chat-target="content"]',
    );

    // Start typewriter for this container (blinking cursor serves as typing indicator)
    this.startTypewriter(contentContainer);

    // Scroll to bottom so the cursor is fully visible
    this.scrollToBottom();

    // Allow an in-flight stream to be cancelled (e.g. via "New chat")
    this.streamAbortController = new AbortController();

    try {
      const response = await fetch(this.streamUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          session_id: this.sessionIdValue,
          session_token: this.sessionTokenValue,
          message: message,
        }),
        signal: this.streamAbortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Process complete lines, keep incomplete last line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              this.handleStreamChunk(parsed);
            } catch {
              // Ignore JSON parse errors for incomplete chunks
            }
          }
        }
      }
      // Fallback: if stream ends without 'done' message, finish typewriter
      if (this.currentStreamContent && this.typewriterContainer) {
        this.typewriterStreamDone = true;
      }
    } catch (error) {
      // Aborted streams (e.g. user started a new chat) are intentional — stay quiet
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error in streaming:', error);
      this.finishTypewriter();
      this.showError(this.errorReceiveFailedValue);
    } finally {
      this.streamAbortController = null;
      this.setLoading(false);
    }
  }

  handleStreamChunk(chunk) {
    switch (chunk.type) {
      case 'delta':
        this.currentStreamContent += chunk.content;
        this.feedTypewriter(chunk.content);
        break;
      case 'done':
        // Store debug data on the assistant message element if available
        if (this.debugEnabledValue && chunk.debug) {
          this.pendingDebugData = chunk.debug;
        }
        // Signal typewriter that stream is complete — let it flush naturally
        this.typewriterStreamDone = true;
        // Keep the sidebar in sync (new session appears, ordering/title update)
        this.refreshSessionList();
        break;
      case 'error':
        this.finishTypewriter();
        this.showError(chunk.content || this.errorGenericValue);
        break;
    }
  }

  appendMessage(role, content, debugData = null) {
    const messageDiv = this.createMessageElement(role, content);

    // Attach debug data for non-streaming messages
    if (this.debugEnabledValue && debugData && role === 'assistant') {
      messageDiv.dataset.debug = JSON.stringify(debugData);
      this.addDebugIcon(messageDiv);
    }

    this.messagesTarget.appendChild(messageDiv);

    if (role === 'assistant') {
      this.addFeedbackButtons(messageDiv);
      this.scrollToMessage(messageDiv);
    } else {
      this.scrollToBottom();
    }
  }

  createMessageElement(role, content) {
    const div = document.createElement('div');
    div.className = `chat-message chat-message--${role} flex ${role === 'user' ? 'justify-end' : 'justify-start'} mb-4`;

    const innerDiv = document.createElement('div');
    innerDiv.className = `max-w-[80%] rounded-lg px-4 py-3 ${
      role === 'user'
        ? 'bg-accent text-on-accent rounded-br-none'
        : 'bg-chat-panel text-chat-text rounded-bl-none border border-chat-border'
    }`;

    const contentContainer = document.createElement('div');
    contentContainer.setAttribute('data-chat-target', 'content');
    contentContainer.className = 'leading-relaxed chat-content';

    // Parse markdown for assistant messages, plain text for user messages
    if (role === 'assistant') {
      contentContainer.innerHTML = this.parseMarkdown(content);
      // Highlight trailing question, but skip the first assistant message (greeting)
      const isFirstAssistant =
        this.hasMessagesTarget &&
        !this.messagesTarget.querySelector('.chat-message--assistant');
      if (!isFirstAssistant && content && content.trim().endsWith('?')) {
        const paragraphs = contentContainer.querySelectorAll('p');
        if (paragraphs.length > 0) {
          const lastP = paragraphs[paragraphs.length - 1];
          if (lastP.textContent.trim().endsWith('?')) {
            lastP.classList.add('chat-question-highlight');
          }
        }
      }
    } else {
      contentContainer.className += ' whitespace-pre-wrap';
      contentContainer.textContent = content;
    }

    innerDiv.appendChild(contentContainer);
    div.appendChild(innerDiv);

    return div;
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className =
      'chat-error bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-2 rounded mb-4';
    errorDiv.textContent = message;
    this.messagesTarget.appendChild(errorDiv);
    this.scrollToBottom();

    // Auto-remove after 5 seconds
    setTimeout(() => errorDiv.remove(), 5000);
  }

  setLoading(loading) {
    this.isLoading = loading;

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = loading;
    }
  }

  showTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.remove('hidden');
    }
  }

  hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add('hidden');
    }
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  scrollToMessage(element) {
    if (element && this.hasMessagesTarget) {
      // Scroll the messages container so the element appears at the top
      this.messagesTarget.scrollTop =
        element.offsetTop - this.messagesTarget.offsetTop;
    }
  }

  handleUserScroll() {
    if (!this.hasMessagesTarget) return;
    const { scrollTop, scrollHeight, clientHeight } = this.messagesTarget;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    this.userHasScrolledUp = distanceFromBottom > 50;
  }

  autoScrollDuringStream() {
    if (!this.userHasScrolledUp) {
      this.scrollToBottom();
    }
  }

  // Handle Enter key to submit
  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit(event);
    }
  }

  // ============================================
  // Typewriter Effect
  // ============================================

  startTypewriter(container) {
    // Flush any previous typewriter
    if (this.typewriterContainer) {
      this.finishTypewriter();
    }

    this.typewriterBuffer = '';
    this.typewriterRendered = 0;
    this.typewriterStreamDone = false;
    this.typewriterContainer = container;
    this.typewriterLastTimestamp = 0;

    // Add cursor class
    container.classList.add('typewriter-active');

    // Start render loop
    this.typewriterAnimationId = requestAnimationFrame(
      this.typewriterTick.bind(this),
    );
  }

  feedTypewriter(text) {
    this.typewriterBuffer += text;
  }

  typewriterTick(timestamp) {
    if (!this.typewriterContainer) return;

    // Throttle to ~20ms intervals
    if (timestamp - this.typewriterLastTimestamp < 20) {
      this.typewriterAnimationId = requestAnimationFrame(
        this.typewriterTick.bind(this),
      );
      return;
    }
    this.typewriterLastTimestamp = timestamp;

    const remaining = this.typewriterBuffer.length - this.typewriterRendered;

    if (remaining > 0) {
      // When stream is still active but buffer is low, wait for more tokens
      // to accumulate. This prevents the stuttery burst-pause pattern when
      // the LLM slows down (e.g. at the end of a response).
      if (
        !this.typewriterStreamDone &&
        remaining < 5 &&
        this.typewriterRendered > 0
      ) {
        this.typewriterAnimationId = requestAnimationFrame(
          this.typewriterTick.bind(this),
        );
        return;
      }

      // Adaptive chunk size: catch up if buffer is large
      const chunkSize = remaining > 200 ? Math.ceil(remaining / 10) : 1;
      const next = this.typewriterBuffer.slice(
        this.typewriterRendered,
        this.typewriterRendered + chunkSize,
      );
      this.typewriterRendered += next.length;

      this.typewriterContainer.textContent = this.typewriterBuffer.slice(
        0,
        this.typewriterRendered,
      );

      // Auto-scroll during typing
      this.autoScrollDuringStream();
    }

    // Check if we're done: stream finished AND buffer fully rendered
    if (
      this.typewriterStreamDone &&
      this.typewriterRendered >= this.typewriterBuffer.length
    ) {
      this.finishTypewriter();
      return;
    }

    this.typewriterAnimationId = requestAnimationFrame(
      this.typewriterTick.bind(this),
    );
  }

  finishTypewriter() {
    if (!this.typewriterContainer) return;

    const container = this.typewriterContainer;

    // Cancel animation
    if (this.typewriterAnimationId) {
      cancelAnimationFrame(this.typewriterAnimationId);
      this.typewriterAnimationId = null;
    }

    // Remove cursor
    container.classList.remove('typewriter-active');

    // Parse markdown and render final content
    if (this.currentStreamContent) {
      container.innerHTML = this.parseMarkdown(this.currentStreamContent);
      this.highlightQuestion(container);
    }

    // Attach debug data if available
    if (
      this.debugEnabledValue &&
      this.pendingDebugData &&
      this.currentAssistantDiv
    ) {
      this.currentAssistantDiv.dataset.debug = JSON.stringify(
        this.pendingDebugData,
      );
      this.addDebugIcon(this.currentAssistantDiv);
      this.pendingDebugData = null;
    }

    // Add feedback buttons after streaming completes
    if (this.currentAssistantDiv) {
      this.addFeedbackButtons(this.currentAssistantDiv);
    }
    this.currentAssistantDiv = null;

    // Final scroll after markdown rendering changes element height
    this.scrollToBottom();

    // Cleanup state
    this.typewriterContainer = null;
    this.typewriterBuffer = '';
    this.typewriterRendered = 0;
  }

  // ============================================
  // Feedback Buttons
  // ============================================

  addFeedbackButtons(messageDiv) {
    if (this.feedbackSubmitted) return;

    // Don't add to the greeting (first assistant message with no prior user message)
    const hasUserMessage =
      this.hasMessagesTarget &&
      this.messagesTarget.querySelector('.chat-message--user');
    if (!hasUserMessage) return;

    // Remove previous feedback rows (only latest message gets buttons)
    const existing = this.messagesTarget?.querySelectorAll(
      '.chat-feedback-row',
    );
    if (existing) existing.forEach((el) => el.remove());

    const labels = this.feedbackLabelsValue;
    const row = document.createElement('div');
    row.className =
      'chat-feedback-row flex items-center gap-2 mt-2 ml-1 text-sm text-chat-muted';

    const prompt = document.createElement('span');
    prompt.textContent = labels.prompt || 'Was this helpful?';
    row.appendChild(prompt);

    const upBtn = this.createFeedbackButton(
      'positive',
      '👍',
      labels.thumbs_up || 'Helpful',
    );
    const downBtn = this.createFeedbackButton(
      'negative',
      '👎',
      labels.thumbs_down || 'Not helpful',
    );

    row.appendChild(upBtn);
    row.appendChild(downBtn);

    // Insert feedback row after the message div
    messageDiv.after(row);
  }

  createFeedbackButton(sentiment, emoji, label) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className =
      'chat-feedback-btn px-2 py-1 rounded text-xs border border-chat-border hover:bg-chat-elevated hover:text-chat-text transition-colors cursor-pointer';
    btn.textContent = `${emoji} ${label}`;
    btn.dataset.sentiment = sentiment;
    btn.addEventListener('click', () => {
      const feedbackRow = btn.closest('.chat-feedback-row');
      this.handleFeedbackClick(sentiment, feedbackRow);
    });
    return btn;
  }

  handleFeedbackClick(sentiment, feedbackRow) {
    if (sentiment === 'positive') {
      this.submitFeedback(sentiment, null, feedbackRow);
    } else {
      this.showFeedbackTextInput(feedbackRow, sentiment);
    }
  }

  showFeedbackTextInput(feedbackRow, sentiment) {
    const labels = this.feedbackLabelsValue;

    feedbackRow.innerHTML = '';
    feedbackRow.className =
      'chat-feedback-row flex flex-col gap-2 mt-2 ml-1 text-sm';

    const promptEl = document.createElement('span');
    promptEl.className = 'text-chat-muted';
    promptEl.textContent =
      labels.negative_prompt || 'What can we improve?';
    feedbackRow.appendChild(promptEl);

    const inputRow = document.createElement('div');
    inputRow.className = 'flex items-center gap-2';

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder =
      labels.text_placeholder || 'Optional feedback...';
    input.className =
      'flex-1 px-3 py-1.5 bg-chat-panel text-chat-text placeholder-chat-faint border border-chat-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-accent';

    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className =
      'px-3 py-1.5 bg-accent text-on-accent text-xs font-semibold rounded hover:bg-accent transition-colors cursor-pointer';
    submitBtn.textContent = labels.submit || 'Submit';
    submitBtn.addEventListener('click', () => {
      this.submitFeedback(sentiment, input.value || null, feedbackRow);
    });

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className =
      'px-3 py-1.5 text-chat-muted text-xs hover:text-chat-text transition-colors cursor-pointer';
    skipBtn.textContent = labels.skip || 'Skip';
    skipBtn.addEventListener('click', () => {
      this.submitFeedback(sentiment, null, feedbackRow);
    });

    inputRow.appendChild(input);
    inputRow.appendChild(submitBtn);
    inputRow.appendChild(skipBtn);
    feedbackRow.appendChild(inputRow);

    input.focus();
  }

  async submitFeedback(sentiment, text, feedbackRow) {
    this.feedbackSubmitted = true;

    const payload = {
      conversation_id: this.sessionIdValue || this.sessionTokenValue,
      source: 'internal',
      feedback: {
        type: 'thumb',
        feedback: sentiment,
        text: text,
      },
      session_data: {
        conversationId: this.sessionIdValue,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
    };

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content');

      await fetch(this.feedbackUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body: JSON.stringify(payload),
      });
    } catch {
      // Silently fail — feedback is best-effort
    }

    this.showFeedbackConfirmation(feedbackRow, sentiment);
  }

  showFeedbackConfirmation(feedbackRow, sentiment) {
    const labels = this.feedbackLabelsValue;
    feedbackRow.innerHTML = '';
    feedbackRow.className =
      'chat-feedback-row flex items-center gap-2 mt-2 ml-1 text-sm text-chat-muted';

    const emoji = sentiment === 'positive' ? '👍' : '👎';
    const msg =
      sentiment === 'positive'
        ? labels.thanks_positive || 'Thanks for your feedback!'
        : labels.thanks_negative || 'Thanks, we will improve!';

    const span = document.createElement('span');
    span.textContent = `${emoji} ${msg}`;
    feedbackRow.appendChild(span);

    // Fade out after 3 seconds
    setTimeout(() => {
      feedbackRow.style.transition = 'opacity 0.5s';
      feedbackRow.style.opacity = '0';
      setTimeout(() => feedbackRow.remove(), 500);
    }, 3000);
  }

  // ============================================
  // Question Highlighting
  // ============================================

  highlightQuestion(container) {
    const text = this.currentStreamContent || '';
    if (!text.trim().endsWith('?')) return;

    // Find the last paragraph or text node that contains the question
    const paragraphs = container.querySelectorAll('p');
    if (paragraphs.length === 0) return;

    const lastP = paragraphs[paragraphs.length - 1];
    const lastPText = lastP.textContent.trim();

    if (lastPText.endsWith('?')) {
      lastP.classList.add('chat-question-highlight');
    }
  }

  // ============================================
  // Markdown Parsing
  // ============================================

  /**
   * Parse simple markdown to HTML
   * Supports: **bold**, *italic*, lists (* item), and newlines
   */
  parseMarkdown(text) {
    if (!text) return '';

    // Escape HTML to prevent XSS
    let html = this.escapeHtml(text);

    // Parse **bold** (must be done before *italic* to avoid conflicts)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    // Parse *italic* (single asterisks not followed/preceded by another asterisk)
    html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');

    // Parse bullet lists: lines starting with "* " or "- "
    // First, identify list blocks and wrap them
    const lines = html.split('\n');
    let result = [];
    let inList = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isListItem = /^\s*[*-]\s+/.test(line);

      if (isListItem) {
        if (!inList) {
          result.push('<ul class="list-disc list-inside my-2 space-y-1">');
          inList = true;
        }
        // Remove the bullet and wrap in li
        const content = line.replace(/^\s*[*-]\s+/, '');
        result.push(`<li>${content}</li>`);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(line);
      }
    }

    // Close any open list
    if (inList) {
      result.push('</ul>');
    }

    html = result.join('\n');

    // Convert newlines to <br> (but not within lists)
    // Replace double newlines with paragraph breaks
    html = html.replace(/\n\n/g, '</p><p class="mt-3">');
    // Replace single newlines with <br> (except after list items)
    html = html.replace(/(?<!<\/li>|<\/ul>)\n(?!<ul|<li)/g, '<br>');

    // Wrap in paragraph if not starting with a list
    if (!html.startsWith('<ul')) {
      html = `<p>${html}</p>`;
    }

    // Clean up empty paragraphs
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
  }

  /**
   * Escape HTML special characters to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ============================================
  // Debug Mode
  // ============================================

  addDebugIcon(messageDiv) {
    const innerDiv = messageDiv.querySelector('.max-w-\\[80\\%\\]');
    if (!innerDiv) return;

    // Make container relative for absolute positioning
    innerDiv.classList.add('relative');

    const icon = document.createElement('button');
    icon.type = 'button';
    icon.className =
      'absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded text-chat-faint hover:text-accent hover:bg-chat-elevated transition-colors text-xs';
    icon.title = this.debugLabelsValue.show_info || 'Show debug info';
    icon.innerHTML =
      '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>';
    icon.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleDebug(messageDiv);
    });

    innerDiv.appendChild(icon);
  }

  toggleDebug(messageDiv) {
    // Check if debug panel already exists
    const existing = messageDiv.querySelector('.debug-panel');
    if (existing) {
      existing.remove();
      return;
    }

    const debugJson = messageDiv.dataset.debug;
    if (!debugJson) return;

    let debugData;
    try {
      debugData = JSON.parse(debugJson);
    } catch {
      return;
    }

    const panel = document.createElement('div');
    panel.className =
      'debug-panel mt-3 bg-chat-bg border border-chat-border rounded-lg p-3 max-h-80 overflow-auto';

    // Header with message count
    const header = document.createElement('div');
    header.className =
      'flex items-center justify-between mb-2 text-xs text-chat-muted';
    header.innerHTML = `<span class="font-semibold">LLM Debug Info</span><span>${debugData.messages_count || '?'} messages</span>`;
    panel.appendChild(header);

    const dl = this.debugLabelsValue;

    // Classification info (new taxonomy-driven format)
    if (debugData.classification && debugData.classification.life_area) {
      const cls = debugData.classification;
      const clsSection = document.createElement('div');
      clsSection.className = 'mb-3';

      // Classification tree header
      const treeHeader = document.createElement('div');
      treeHeader.className = 'text-xs text-chat-faint uppercase tracking-wider mb-1.5 font-semibold';
      treeHeader.textContent = 'Classification';
      clsSection.appendChild(treeHeader);

      // Tree structure: life_area > sub_contexts
      const treeContainer = document.createElement('div');
      treeContainer.className = 'border-l-2 border-accent pl-3 ml-1';

      // Life area row
      const areaRow = document.createElement('div');
      areaRow.className = 'flex items-center gap-2 mb-1';
      const areaLabel = (dl.life_area_labels && dl.life_area_labels[cls.life_area]) || cls.life_area;
      areaRow.innerHTML = `<span class="inline-block w-2 h-2 rounded-full bg-accent flex-shrink-0"></span>
        <span class="text-sm font-medium text-accent">${this.escapeHtml(areaLabel)}</span>`;
      treeContainer.appendChild(areaRow);

      // Life area matched keywords
      if (cls.matched_keywords && cls.matched_keywords.life_area && cls.matched_keywords.life_area.length > 0) {
        const kwRow = document.createElement('div');
        kwRow.className = 'ml-4 mb-1.5 flex flex-wrap gap-1';
        cls.matched_keywords.life_area.forEach((kw) => {
          const tag = document.createElement('span');
          tag.className = 'inline-block px-1.5 py-0.5 text-[10px] rounded bg-accent text-on-accent border border-accent';
          tag.textContent = kw;
          kwRow.appendChild(tag);
        });
        treeContainer.appendChild(kwRow);
      }

      // Sub-contexts
      if (cls.sub_contexts && cls.sub_contexts.length > 0) {
        cls.sub_contexts.forEach((sc) => {
          const scRow = document.createElement('div');
          scRow.className = 'flex items-center gap-2 mb-0.5 ml-3';
          const scLabel = (dl.sub_context_labels && dl.sub_context_labels[sc]) || sc;
          scRow.innerHTML = `<span class="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
            <span class="text-xs text-blue-300">${this.escapeHtml(scLabel)}</span>`;
          treeContainer.appendChild(scRow);

          // Sub-context matched keywords
          if (cls.matched_keywords && cls.matched_keywords.sub_contexts && cls.matched_keywords.sub_contexts[sc]) {
            const scKwRow = document.createElement('div');
            scKwRow.className = 'ml-7 mb-1 flex flex-wrap gap-1';
            cls.matched_keywords.sub_contexts[sc].forEach((kw) => {
              const tag = document.createElement('span');
              tag.className = 'inline-block px-1.5 py-0.5 text-[10px] rounded bg-blue-900/20 text-blue-400/70 border border-blue-800/30';
              tag.textContent = kw;
              scKwRow.appendChild(tag);
            });
            treeContainer.appendChild(scKwRow);
          }
        });
      }

      clsSection.appendChild(treeContainer);

      // Confidence bar + method badge
      const metaRow = document.createElement('div');
      metaRow.className = 'flex items-center gap-3 mt-2';
      if (cls.confidence !== undefined) {
        const pct = Math.round(cls.confidence * 100);
        const barColor = pct > 70 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
        metaRow.innerHTML += `<div class="flex items-center gap-1.5">
          <div class="w-16 h-1.5 rounded-full bg-chat-elevated overflow-hidden"><div class="${barColor} h-full rounded-full" style="width:${pct}%"></div></div>
          <span class="text-xs text-chat-muted">${pct}%</span></div>`;
      }
      if (cls.method) {
        const methodColors = { keyword: 'text-green-400 bg-green-900/30 border-green-700/40', llm: 'text-blue-400 bg-blue-900/30 border-blue-700/40', fallback: 'text-red-400 bg-red-900/30 border-red-700/40' };
        const mCls = methodColors[cls.method] || 'text-chat-muted bg-chat-panel border-chat-border';
        metaRow.innerHTML += `<span class="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded border ${mCls}">${this.escapeHtml(cls.method)}</span>`;
      }
      clsSection.appendChild(metaRow);

      panel.appendChild(clsSection);

      // Tier-1 / Tier-2 score tables (collapsible) — same data the admin shows
      if (
        (cls.life_area_scores && cls.life_area_scores.length) ||
        (cls.sub_context_scores && cls.sub_context_scores.length)
      ) {
        this._renderTaxonomyTierTables(panel, cls);
      }
    } else if (
      debugData.classifications &&
      debugData.classifications.length > 0
    ) {
      // Legacy classification format (array of strings)
      const badgeRow = document.createElement('div');
      badgeRow.className = 'flex flex-wrap gap-1 mb-2';
      debugData.classifications.forEach((cls) => {
        const badge = document.createElement('span');
        badge.className =
          'inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-accent text-on-accent border border-accent';
        badge.textContent = cls;
        badgeRow.appendChild(badge);
      });
      panel.appendChild(badgeRow);
    }

    // Session type + prompt source
    if (debugData.session_type || debugData.prompt_source) {
      const metaRow = document.createElement('div');
      metaRow.className = 'text-xs text-chat-faint mb-2';
      const parts = [];
      if (debugData.session_type)
        parts.push(`Session: ${debugData.session_type}`);
      if (debugData.prompt_source)
        parts.push(`Prompt: ${debugData.prompt_source}`);
      metaRow.textContent = parts.join(' | ');
      panel.appendChild(metaRow);
    }

    // Pillar table (if context_reasoning present)
    if (debugData.context_reasoning && debugData.context_reasoning.pillars) {
      const pillars = debugData.context_reasoning.pillars;
      const table = document.createElement('table');
      table.className = 'w-full text-xs text-chat-text mb-2 border-collapse';
      table.innerHTML = `
        <thead>
          <tr class="text-chat-faint border-b border-chat-border">
            <th class="text-left py-1 pr-2">${this.escapeHtml(dl.pillar || 'Pillar')}</th>
            <th class="text-left py-1 pr-2">${this.escapeHtml(dl.present || 'Present')}</th>
            <th class="text-left py-1 pr-2">${this.escapeHtml(dl.included || 'Included')}</th>
            <th class="text-left py-1">${this.escapeHtml(dl.value || 'Value')}</th>
          </tr>
        </thead>
        <tbody>
          ${['year', 'month', 'day', 'hour']
            .map((key) => {
              const p = pillars[key] || {};
              const labelMap = {
                year: dl.year || 'Year',
                month: dl.month || 'Month',
                day: dl.day || 'Day',
                hour: dl.hour || 'Hour',
              };
              return `<tr class="border-b border-chat-border">
              <td class="py-1 pr-2 font-medium">${this.escapeHtml(labelMap[key])}</td>
              <td class="py-1 pr-2">${p.present ? '✓' : '—'}</td>
              <td class="py-1 pr-2">${p.included ? '<span class="text-green-400">✓</span>' : '<span class="text-chat-faint">—</span>'}</td>
              <td class="py-1">${p.formatted || '—'}</td>
            </tr>`;
            })
            .join('')}
        </tbody>
      `;
      panel.appendChild(table);

      // Pattern + Typus
      const reasoning = debugData.context_reasoning;
      if (reasoning.main_mental_pattern || reasoning.typus_analysis) {
        const patternDiv = document.createElement('div');
        patternDiv.className = 'text-xs text-chat-muted mb-2';
        const infoParts = [];
        const patternLabel = dl.pattern || 'Pattern';
        const typusLabel = dl.typus || 'Type';
        if (reasoning.main_mental_pattern)
          infoParts.push(`${patternLabel}: ${reasoning.main_mental_pattern}`);
        if (reasoning.typus_analysis && reasoning.typus_analysis.present)
          infoParts.push(
            `${typusLabel}: ${reasoning.typus_analysis.basic_pattern || '—'} (${reasoning.typus_analysis.type || '—'})`,
          );
        patternDiv.textContent = infoParts.join(' | ');
        panel.appendChild(patternDiv);
      }

      // Cast V3 visibility block — shows api_version, V3 fields read,
      // balance, talent profile, resolved docs, rule rejections.
      this._renderCastV3Section(panel, reasoning, debugData);
    }

    // Collapsible raw JSON
    const details = document.createElement('details');
    details.className = 'mt-2';
    const summary = document.createElement('summary');
    summary.className =
      'text-xs text-chat-faint cursor-pointer hover:text-chat-text';
    summary.textContent = 'Raw JSON';
    details.appendChild(summary);

    const pre = document.createElement('pre');
    pre.className =
      'text-xs text-green-400 font-mono whitespace-pre-wrap break-all mt-1';
    pre.textContent = JSON.stringify(debugData, null, 2);
    details.appendChild(pre);
    panel.appendChild(details);

    // Insert debug panel after the inner div (inside the message container)
    const innerDiv = messageDiv.querySelector('.max-w-\\[80\\%\\]');
    if (innerDiv) {
      innerDiv.appendChild(panel);
    }
  }

  // Renders the Cast V3 block in the chat debug panel: cast version badge,
  // resolved V3 paths/values, balance + talent profile, resolved doc files,
  // and any sub-contexts dropped by a balance/cast `rule:`.
  _renderCastV3Section(panel, reasoning, debugData) {
    const section = document.createElement('div');
    section.className = 'mt-3 pt-2 border-t border-chat-border';

    const header = document.createElement('div');
    header.className = 'text-xs text-chat-faint uppercase tracking-wider mb-1.5 font-semibold';
    header.textContent = 'Cast';
    section.appendChild(header);

    // Cast version badge
    if (reasoning.cast_id) {
      const apiVersion = reasoning.cast_api_version || 'v2';
      const isV3 = apiVersion === 'v3';
      const colorClass = isV3
        ? 'bg-teal-900/40 text-teal-300 border-teal-700/40'
        : 'bg-orange-900/40 text-orange-300 border-orange-700/40';
      const badgeRow = document.createElement('div');
      badgeRow.className = 'mb-2';
      badgeRow.innerHTML = `<span class="inline-block px-2 py-0.5 text-[10px] font-medium rounded border ${colorClass}">
        ${this.escapeHtml(reasoning.cast_class || 'Cast')} #${reasoning.cast_id} (API ${apiVersion.toUpperCase()})
      </span>`;
      section.appendChild(badgeRow);
    }

    // Personality balance + talent profile
    const summaryParts = [];
    if (reasoning.personality_balance) {
      summaryParts.push(`Personality Balance: <strong>${this.escapeHtml(reasoning.personality_balance)}</strong>`);
    }
    if (reasoning.talent_main_profile) {
      const tb = reasoning.talent_balance ? ` (${this.escapeHtml(reasoning.talent_balance)})` : '';
      summaryParts.push(`Talent Profile: <strong>${this.escapeHtml(reasoning.talent_main_profile)}</strong>${tb}`);
    }
    if (summaryParts.length > 0) {
      const summary = document.createElement('div');
      summary.className = 'text-xs text-chat-muted mb-2';
      summary.innerHTML = summaryParts.join(' | ');
      section.appendChild(summary);
    }

    // Resolved V3 cast paths that fed the prompt
    const resolved = (reasoning.resolved_cast_paths || (debugData.classification && debugData.classification.resolved_cast_paths)) || [];
    if (resolved.length > 0) {
      const label = document.createElement('div');
      label.className = 'text-xs text-chat-faint mb-1';
      label.textContent = 'Cast-Felder verwendet:';
      section.appendChild(label);

      const list = document.createElement('div');
      list.className = 'flex flex-wrap gap-1 mb-2';
      resolved.forEach((entry) => {
        const path = entry.path || '';
        let value = entry.value;
        if (value && typeof value === 'object') value = JSON.stringify(value);
        const tag = document.createElement('code');
        tag.className = 'inline-block px-1.5 py-0.5 text-[10px] rounded bg-chat-panel text-chat-text border border-chat-border';
        tag.textContent = `${path} = ${value === null || value === undefined ? '∅' : value}`;
        list.appendChild(tag);
      });
      section.appendChild(list);
    }

    // Documents loaded
    const documents = (reasoning.documents_loaded || (debugData.classification && debugData.classification.documents_loaded)) || [];
    if (documents.length > 0) {
      const label = document.createElement('div');
      label.className = 'text-xs text-chat-faint mb-1';
      label.textContent = 'Dokumente:';
      section.appendChild(label);

      const list = document.createElement('div');
      list.className = 'flex flex-col gap-0.5 mb-2';
      documents.forEach((doc) => {
        const found = doc.found;
        const icon = found ? '<span class="text-green-400">✓</span>' : '<span class="text-red-400">✗</span>';
        const path = doc.resolved || doc.pattern;
        const err = doc.lookup_error ? ` <span class="text-red-400/70">(${this.escapeHtml(doc.lookup_error)})</span>` : '';
        const row = document.createElement('div');
        row.className = 'text-[10px] text-chat-muted font-mono';
        row.innerHTML = `${icon} ${this.escapeHtml(path || '?')}${err}`;
        list.appendChild(row);
      });
      section.appendChild(list);
    }

    // Sub-contexts dropped by a balance/cast rule
    const rejected = (debugData.classification && debugData.classification.rejected_by_rule) || (reasoning.classification && reasoning.classification.rejected_by_rule) || [];
    if (rejected.length > 0) {
      const label = document.createElement('div');
      label.className = 'text-xs text-red-400/80 mb-1 font-semibold';
      label.textContent = 'Verworfen (Regel):';
      section.appendChild(label);

      const list = document.createElement('div');
      list.className = 'flex flex-col gap-0.5';
      rejected.forEach((entry) => {
        const sc = entry.sub_context || '';
        const reason = entry.reason || '';
        const row = document.createElement('div');
        row.className = 'text-[10px] text-red-300/80';
        row.textContent = `${sc} – ${reason}`;
        list.appendChild(row);
      });
      section.appendChild(list);
    }

    if (section.children.length > 1) {
      panel.appendChild(section);
    }
  }

  // Renders the Tier-1 (Lebensbereich) and Tier-2 (Sub-Kontext) score tables
  // in a collapsible block. Mirrors the chat taxonomy sandbox's "Ergebnis"
  // panel so admins see identical numbers in the widget and the sandbox.
  _renderTaxonomyTierTables(panel, cls) {
    const details = document.createElement('details');
    details.className = 'mt-3 pt-2 border-t border-chat-border';

    const summary = document.createElement('summary');
    summary.className =
      'text-xs text-chat-faint uppercase tracking-wider cursor-pointer hover:text-chat-text font-semibold';
    summary.textContent = 'Tier 1 / Tier 2 Scores';
    details.appendChild(summary);

    const wrap = document.createElement('div');
    wrap.className = 'mt-2 space-y-2';

    if (cls.elapsed_ms !== undefined && cls.elapsed_ms !== null) {
      const t = document.createElement('div');
      t.className = 'text-[10px] text-chat-faint';
      t.textContent = `Klassifizierungs-Zeit: ${cls.elapsed_ms} ms`;
      wrap.appendChild(t);
    }

    if (cls.life_area_scores && cls.life_area_scores.length) {
      wrap.appendChild(this._buildTier1Table(cls.life_area_scores));
    }

    if (cls.sub_context_scores && cls.sub_context_scores.length) {
      wrap.appendChild(
        this._buildTier2Table(cls.sub_context_scores, cls.life_area),
      );
    }

    details.appendChild(wrap);
    panel.appendChild(details);
  }

  _buildTier1Table(rows) {
    const wrap = document.createElement('div');

    const heading = document.createElement('div');
    heading.className = 'text-[11px] text-chat-muted font-semibold mb-1';
    heading.textContent = 'Tier 1 — Lebensbereich-Scores';
    wrap.appendChild(heading);

    const table = document.createElement('table');
    table.className = 'w-full text-xs text-chat-text border-collapse';

    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr class="text-chat-faint border-b border-chat-border">
        <th class="text-left py-1 pr-2">Code</th>
        <th class="text-left py-1 pr-2">Lebensbereich</th>
        <th class="text-left py-1 pr-2" title="Score nur aus den eigenen Lebensbereichs-Keywords">Eigene</th>
        <th class="text-left py-1 pr-2" title="Score inkl. aller Sub-Kontext-Keywords">Rollup</th>
        <th class="text-left py-1">Matches</th>
      </tr>
    `;
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    rows.forEach((row) => {
      tbody.appendChild(this._buildTier1Row(row));
    });
    table.appendChild(tbody);

    wrap.appendChild(table);
    return wrap;
  }

  _buildTier1Row(row) {
    const tr = document.createElement('tr');
    tr.className = 'border-b border-chat-border align-top';

    const score = Number(row.score) || 0;
    const rollup = Number(row.rollup_score) || 0;
    const ownMatches = Array.isArray(row.matched) ? row.matched : [];
    const rollupMatches = Array.isArray(row.rollup_matched)
      ? row.rollup_matched
      : [];
    const inherited = rollupMatches.filter((kw) => !ownMatches.includes(kw));

    const inactive =
      row.available === false
        ? '<span class="ml-1 inline-block px-1 py-0 text-[9px] rounded bg-chat-elevated text-chat-muted">inaktiv</span>'
        : '';

    const rollupExtra =
      rollup > score
        ? `<span class="text-chat-faint text-[9px] ml-1">(+${(rollup - score).toFixed(2)})</span>`
        : '';

    tr.innerHTML = `
      <td class="py-1 pr-2 font-mono text-accent">${this.escapeHtml(row.letter || '')}</td>
      <td class="py-1 pr-2">${this.escapeHtml(row.label_de || row.key || '')}${inactive}</td>
      <td class="py-1 pr-2 font-mono">${score.toFixed(2)}</td>
      <td class="py-1 pr-2 font-mono font-semibold">${rollup.toFixed(2)}${rollupExtra}</td>
      <td class="py-1">${this._renderTierMatches(ownMatches, inherited)}</td>
    `;
    return tr;
  }

  _renderTierMatches(ownMatches, inherited) {
    const ownTags = ownMatches
      .slice(0, 3)
      .map(
        (kw) =>
          `<code class="inline-block px-1 py-0 text-[10px] rounded bg-accent text-on-accent border border-accent mr-1">${this.escapeHtml(kw)}</code>`,
      )
      .join('');
    const inheritedTags = inherited
      .slice(0, 3)
      .map(
        (kw) =>
          `<code title="aus Sub-Kontext geerbt" class="inline-block px-1 py-0 text-[10px] rounded bg-yellow-900/30 text-yellow-300/80 border border-yellow-700/30 mr-1">${this.escapeHtml(kw)}</code>`,
      )
      .join('');
    const hidden =
      Math.max(ownMatches.length - 3, 0) + Math.max(inherited.length - 3, 0);
    const overflow =
      hidden > 0
        ? `<span class="text-[10px] text-chat-faint ml-1">+${hidden}</span>`
        : '';
    return ownTags + inheritedTags + overflow;
  }

  _buildTier2Table(rows, lifeAreaKey) {
    const wrap = document.createElement('div');

    const heading = document.createElement('div');
    heading.className = 'text-[11px] text-chat-muted font-semibold mb-1';
    heading.textContent = `Tier 2 — Sub-Kontext-Scores${lifeAreaKey ? ` (in ${lifeAreaKey})` : ''}`;
    wrap.appendChild(heading);

    const table = document.createElement('table');
    table.className = 'w-full text-xs text-chat-text border-collapse';

    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr class="text-chat-faint border-b border-chat-border">
        <th class="text-left py-1 pr-2">Sub-Kontext</th>
        <th class="text-left py-1 pr-2">Score</th>
        <th class="text-left py-1">Status</th>
      </tr>
    `;
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    rows.forEach((row) => {
      tbody.appendChild(this._buildTier2Row(row));
    });
    table.appendChild(tbody);

    wrap.appendChild(table);
    return wrap;
  }

  _buildTier2Row(row) {
    const tr = document.createElement('tr');
    tr.className = `border-b border-chat-border align-top ${
      row.rule_rejected ? 'bg-red-900/20' : ''
    }`;

    const score = Number(row.score) || 0;
    const label = this.escapeHtml(row.label_de || row.key || '');
    const keyLine = `<div class="text-[9px] text-chat-faint font-mono">${this.escapeHtml(row.key || '')}</div>`;

    let status;
    if (row.rule_rejected) {
      const reason = row.rejection_reason
        ? ` (${this.escapeHtml(row.rejection_reason)})`
        : '';
      status = `<span class="inline-block px-1.5 py-0 text-[10px] rounded bg-red-900/40 text-red-300 border border-red-700/40">Regel verworfen${reason}</span>`;
    } else if (row.available === false) {
      status =
        '<span class="inline-block px-1.5 py-0 text-[10px] rounded bg-chat-panel text-chat-muted border border-chat-border">inaktiv</span>';
    } else {
      status =
        '<span class="inline-block px-1.5 py-0 text-[10px] rounded bg-green-900/30 text-green-300 border border-green-700/40">ok</span>';
    }

    tr.innerHTML = `
      <td class="py-1 pr-2"><div>${label}</div>${keyLine}</td>
      <td class="py-1 pr-2 font-mono">${score.toFixed(2)}</td>
      <td class="py-1">${status}</td>
    `;
    return tr;
  }
}
